import { useState } from "react";

import { useCreateCustomFieldsFromTemplates } from "../../../../hooks/mutations/admin/hospitalDetails";
import { useCustomFieldTemplates } from "../../../../hooks/queries/admin/hospitalDetails";
import { useToast } from "../../../../hooks/useToast";
import { errorToastMessage } from "../../../../utils/HttpError";
import {
  CheckboxField,
  FieldHint,
  FilterButton,
  LoadingOverlay,
  ModalBodyText,
  ModalContainer,
  ModalForm,
  ModalFormActions,
  ModalTitle,
  SectionCaption,
} from "../../shared";

interface IAddFromTemplatesModalProps {
  hospitalGroupId: number;
  onDone: () => void;
}

/**
 * Adds custom fields from the preset catalogue (EN-5175).
 *
 * `alreadyAdded` comes from the API — it flags the presets whose key this
 * hospital already has — so those are shown, disabled, rather than hidden. An
 * operator looking for "Height" wants to see that it is already there, not find
 * an absence.
 */
export function AddFromTemplatesModal({
  hospitalGroupId,
  onDone,
}: IAddFromTemplatesModalProps) {
  const { showToast } = useToast();
  const { templates, isLoading } = useCustomFieldTemplates(hospitalGroupId);
  const { addFromTemplates, isAddingFromTemplates } =
    useCreateCustomFieldsFromTemplates();

  const [selected, setSelected] = useState<string[]>([]);

  const toggle = (key: string) =>
    setSelected((previous) =>
      previous.includes(key)
        ? previous.filter((entry) => entry !== key)
        : [...previous, key]
    );

  const handleSubmit = async () => {
    if (selected.length === 0) return;

    try {
      await addFromTemplates({ hospitalGroupId, templateKeys: selected });
      showToast(
        selected.length === 1
          ? "The custom field was added."
          : `${selected.length} custom fields were added.`,
        "success"
      );
      onDone();
    } catch (error) {
      showToast(
        errorToastMessage(
          error,
          "There was an error adding the custom fields."
        ),
        "warning"
      );
    }
  };

  const available = templates.filter((template) => !template.alreadyAdded);

  return (
    <ModalContainer>
      <ModalTitle>Add from presets</ModalTitle>
      <ModalForm>
        <ModalBodyText>
          Presets create a definition with its type and validation already set.
        </ModalBodyText>

        {isLoading && <LoadingOverlay>Loading presets...</LoadingOverlay>}

        {!isLoading && templates.length === 0 && (
          <FieldHint>There are no presets.</FieldHint>
        )}

        {!isLoading && templates.length > 0 && available.length === 0 && (
          <FieldHint>
            This hospital already has every preset. Use New field for anything
            else.
          </FieldHint>
        )}

        {templates.map((template) => (
          <div key={template.key}>
            <CheckboxField>
              <input
                type="checkbox"
                checked={selected.includes(template.key)}
                disabled={template.alreadyAdded}
                onChange={() => toggle(template.key)}
              />
              {template.label}
              {template.alreadyAdded ? " — already added" : ""}
            </CheckboxField>
            <SectionCaption>
              {template.key} · {template.type}
              {template.description ? ` · ${template.description}` : ""}
            </SectionCaption>
          </div>
        ))}

        <ModalFormActions>
          <FilterButton
            variant="secondary"
            disabled={isAddingFromTemplates}
            onClick={onDone}
          >
            Cancel
          </FilterButton>
          <FilterButton
            variant="primary"
            disabled={isAddingFromTemplates || selected.length === 0}
            onClick={handleSubmit}
          >
            {isAddingFromTemplates
              ? "Adding..."
              : `Add ${selected.length || ""} ${
                  selected.length === 1 ? "field" : "fields"
                }`.replace("  ", " ")}
          </FilterButton>
        </ModalFormActions>
      </ModalForm>
    </ModalContainer>
  );
}
